import { averageConfidence } from './confidence';
import { haversineDistanceMeters } from './distance';
import type { AnalysisResult, AnalysisStats, DailySummary, DateRange, TimelinePoint, TimelineSegment, TimelineVisit } from './models';
import { overlapsDateRange, toLocalDateKey } from './time';

export function buildDailySummaries(visits: TimelineVisit[], segments: TimelineSegment[]): DailySummary[] {
  const summaries = new Map<string, DailySummary>();

  const ensure = (date: string): DailySummary => {
    const current = summaries.get(date);
    if (current) return current;
    const summary: DailySummary = {
      date,
      distanceMeters: 0,
      activeMinutes: 0,
      stayMinutes: 0,
      visitCount: 0,
      topVisits: [],
      segments: [],
      tags: ['unknown'],
      confidence: 0,
    };
    summaries.set(date, summary);
    return summary;
  };

  for (const visit of visits) {
    const date = toLocalDateKey(visit.startTime);
    const summary = ensure(date);
    summary.stayMinutes += visit.durationMinutes;
    summary.visitCount += 1;
    summary.topVisits.push(visit);
  }

  for (const segment of segments) {
    const date = toLocalDateKey(segment.startTime);
    const summary = ensure(date);
    summary.distanceMeters += segment.distanceMeters;
    summary.activeMinutes += segment.durationMinutes;
    summary.segments.push(segment);
  }

  const ordered = [...summaries.values()].sort((a, b) => a.date.localeCompare(b.date));
  for (const summary of ordered) {
    summary.topVisits = summary.topVisits
      .sort((a, b) => b.durationMinutes - a.durationMinutes)
      .slice(0, 5);
    summary.tags = inferTags(summary);
    summary.confidence = averageConfidence([
      ...summary.topVisits.map((visit) => visit.confidence),
      ...summary.segments.map((segment) => segment.confidence),
    ]);
  }
  return ordered;
}

function inferTags(summary: DailySummary): DailySummary['tags'] {
  const tags: DailySummary['tags'] = [];
  if (summary.distanceMeters > 80_000) tags.push('travel');
  if (summary.distanceMeters > 2_000 && summary.distanceMeters <= 80_000 && summary.visitCount >= 2) tags.push('commute');
  if (summary.visitCount >= 4) tags.push('event_day');
  if (summary.distanceMeters < 500 && summary.visitCount <= 1) tags.push('low_activity');
  return tags.length > 0 ? tags : ['unknown'];
}

export function buildAnalysisStats(
  points: TimelinePoint[],
  visits: TimelineVisit[],
  segments: TimelineSegment[],
): AnalysisStats {
  const sortedPoints = [...points].sort((a, b) => Date.parse(a.timestamp) - Date.parse(b.timestamp));
  return {
    totalDistanceMeters: segments.reduce((sum, segment) => sum + segment.distanceMeters, 0),
    totalStayMinutes: visits.reduce((sum, visit) => sum + visit.durationMinutes, 0),
    totalActiveMinutes: segments.reduce((sum, segment) => sum + segment.durationMinutes, 0),
    visitCount: visits.length,
    pointCount: points.length,
    segmentCount: segments.length,
    dateStart: sortedPoints[0] ? toLocalDateKey(sortedPoints[0].timestamp) : undefined,
    dateEnd: sortedPoints[sortedPoints.length - 1] ? toLocalDateKey(sortedPoints[sortedPoints.length - 1].timestamp) : undefined,
  };
}

export function rankVisits(visits: TimelineVisit[], limit = 10): Array<TimelineVisit & { visitOccurrences: number; totalMinutes: number }> {
  const clusters: Array<TimelineVisit & { visitOccurrences: number; totalMinutes: number }> = [];

  for (const visit of visits) {
    const existing = clusters.find((cluster) => haversineDistanceMeters(cluster.center, visit.center) < 250);
    if (existing) {
      existing.visitOccurrences += 1;
      existing.totalMinutes += visit.durationMinutes;
      existing.durationMinutes = existing.totalMinutes;
      existing.confidence = averageConfidence([existing.confidence, visit.confidence]);
      existing.rawRefs.push(...visit.rawRefs);
    } else {
      clusters.push({ ...visit, visitOccurrences: 1, totalMinutes: visit.durationMinutes });
    }
  }

  return clusters.sort((a, b) => b.totalMinutes - a.totalMinutes).slice(0, limit);
}

export function filterAnalysisByDate(result: AnalysisResult, range: DateRange): AnalysisResult {
  if (!range.start && !range.end) return result;

  const points = result.points.filter((point) => overlapsDateRange(point.timestamp, point.timestamp, range));
  const visits = result.visits.filter((visit) => overlapsDateRange(visit.startTime, visit.endTime, range));
  const segments = result.segments.filter((segment) => overlapsDateRange(segment.startTime, segment.endTime, range));
  const dailySummaries = buildDailySummaries(visits, segments);

  return {
    ...result,
    points,
    visits,
    segments,
    dailySummaries,
    trips: result.trips.filter((trip) => {
      const tripStart = `${trip.startDate}T00:00:00`;
      const tripEnd = `${trip.endDate}T23:59:59`;
      return overlapsDateRange(tripStart, tripEnd, range);
    }),
    stats: buildAnalysisStats(points, visits, segments),
  };
}
